import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./Designer.module.css";
import { sanitizeColorName } from "../../utils/designerUtils";
import {
    sanitizePrintLocationLabel,
} from "../../utils/designerHelpers";
import partIconFallbackImg from "../../assets/designer/logo.png";

const normalizeSearchText = (value) =>
    String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();

const FALLBACK_SELECTED_MARK_COLOR = "#d54ab7";
const PALETTE_PREVIEW_LIMIT = 6;
const STRIPE_PALETTE_PREVIEW_LIMIT = 24;
const COLLAPSIBLE_SECTION_OPTION_LIMIT = 4;
const SECTION_PREVIEW_OPTION_LIMIT = 4;
const getSelectedMarkTextColor = (backgroundColor) => {
    const raw = String(backgroundColor || "").trim();
    const normalized = raw.startsWith("#") ? raw.slice(1) : raw;
    if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return "#ffffff";
    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);
    const luminance = (0.299 * r) + (0.587 * g) + (0.114 * b);
    return luminance > 165 ? "#122033" : "#ffffff";
};

const normalizeColorLabelWithoutNumbers = (value) =>
    String(value || "")
        .replace(/[0-9]+/g, "")
        .replace(/\s+/g, " ")
        .trim();

const buildRingBackground = (ringColors = []) => {
    const colors = (ringColors || [])
        .map((item) => String(item?.code || "").trim())
        .filter(Boolean);
    if (!colors.length) return null;
    if (colors.length === 1) return colors[0];

    const slice = 100 / colors.length;
    const segments = colors.map((color, index) => {
        const start = (index * slice).toFixed(2);
        const end = ((index + 1) * slice).toFixed(2);
        return `${color} ${start}% ${end}%`;
    });
    return `conic-gradient(${segments.join(", ")})`;
};

const formatCustomCutColorSummary = (color = null) => {
    const code = String(color?.code || "").trim();
    const name = String(color?.name || "").trim();
    return name || code || "Sin color";
};

const ColorWarningIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
            d="M12 3.75a8.25 8.25 0 1 0 8.25 8.25A8.26 8.26 0 0 0 12 3.75Zm0 12.6a1.05 1.05 0 1 1 1.05-1.05A1.05 1.05 0 0 1 12 16.35Zm1.05-4.2h-2.1V7.95h2.1Z"
            fill="currentColor"
        />
    </svg>
);

const PaletteChevronIcon = ({ expanded = false }) => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        {expanded ? (
            <path d="M7 14.5L12 9.5L17 14.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        ) : (
            <path d="M7 9.5L12 14.5L17 9.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        )}
    </svg>
);

const hasMissingColorFlag = (option) =>
    (option?.missing || []).some((item) => normalizeSearchText(item) === "color");

const shouldShowMissingColorWarning = (option) =>
    Boolean(
        option?.selected
        && option?.selectedMaterialId
        && !option?.selectedColor
        && (
            option?.requiresManualColorSelection
            || hasMissingColorFlag(option)
            || (option?.availableColors || []).length > 0
        )
    );

export const StripeTutorialContent = () => (
    <div className={styles.stripeTutorialCard}>
        <h5 className={styles.stripeTutorialTitle}>
            <i className="bi bi-magic" aria-hidden="true" /> Cómo dibujar tus tiras
        </h5>
        <div className={styles.stripeTutorialSteps}>
            <span className={styles.stripeTutorialStep}>
                <i className="bi bi-1-circle-fill" aria-hidden="true" /> Elegí herramienta.
            </span>
            <span className={styles.stripeTutorialStep}>
                <i className="bi bi-2-circle-fill" aria-hidden="true" /> Dibujá sobre la prenda.
            </span>
            <span className={styles.stripeTutorialStep}>
                <i className="bi bi-3-circle-fill" aria-hidden="true" /> Aplicá o corregí cuando quieras.
            </span>
        </div>
        <div className={styles.stripeTutorialAnim} aria-hidden="true">
            <span className={styles.stripeTutorialStroke} />
            <span className={styles.stripeTutorialStroke} />
            <span className={styles.stripeTutorialCursor}>
                <i className="bi bi-hand-index-thumb-fill" />
            </span>
        </div>
    </div>
);

const resolveColorId = (color) =>
    color?.color_id
    ?? color?.id
    ?? null;

const collectColorTargetOptions = (option) => {
    if (!option) return [];

    const currentQualifies = Boolean(
        (option.selected || option.required)
        && option.selectedMaterialId
        && Array.isArray(option.availableColors)
        && option.availableColors.length > 0
    );

    const current = currentQualifies
        ? [{ key: String(option.key), colorId: resolveColorId(option.selectedColor), colorCode: option.selectedColor?.code || option.selectedColor?.hex || null }]
        : [];

    const dependents = (option.dependentOptions || []).flatMap((childOption) =>
        collectColorTargetOptions(childOption)
    );

    return [...current, ...dependents];
};

const optionTreeContainsKey = (option, targetKey) => {
    if (!option || !targetKey) return false;
    if (String(option.key) === String(targetKey)) return true;
    return (option.dependentOptions || []).some((childOption) =>
        optionTreeContainsKey(childOption, targetKey)
    );
};

const PartColorModal = ({
    option = null,
    onClose,
    onSelectActivePart,
    onTogglePart,
    onSelectMaterial,
    onFetchColors,
    onTogglePalette,
    onSelectColor,
    onClearColor,
    isDualPaintEnabled,
    onToggleDualPaint,
}) => {
    const [targetPartKeys, setTargetPartKeys] = useState([]);
    const [initialColorByPart, setInitialColorByPart] = useState({});

    useEffect(() => {
        if (!option || typeof document === "undefined") return undefined;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                onClose?.();
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [option, onClose]);

    useEffect(() => {
        if (!option) {
            setTargetPartKeys([]);
            setInitialColorByPart({});
            return;
        }
        const targets = collectColorTargetOptions(option);
        const targetKeys = targets.map((entry) => String(entry.key));
        const initialMap = Object.fromEntries(
            targets.map((entry) => [String(entry.key), entry.colorId])
        );
        setTargetPartKeys(targetKeys);
        setInitialColorByPart(initialMap);
    }, [option]);

    const completionState = useMemo(() => {
        if (!option || !targetPartKeys.length) return { ready: false, complete: false };
        const currentTargets = collectColorTargetOptions(option);
        const currentByKey = Object.fromEntries(
            currentTargets.map((entry) => [String(entry.key), entry.colorId])
        );
        const allCompleted = targetPartKeys.every((partKey) => {
            const initialColorId = initialColorByPart[partKey] ?? null;
            const currentColorId = currentByKey[partKey] ?? null;
            return currentColorId !== null && currentColorId !== initialColorId;
        });
        return { ready: true, complete: allCompleted };
    }, [option, targetPartKeys, initialColorByPart]);

    useEffect(() => {
        if (!completionState.ready) return;
        if (!completionState.complete) return;
        onClose?.();
    }, [completionState, onClose]);

    if (!option) return null;

    return (
        <div className={styles.confirmOverlay} onClick={() => onClose?.()}>
            <div
                className={styles.partOptionModalCard}
                role="dialog"
                aria-modal="true"
                aria-label={`Configurar ${option.label}`}
                onClick={(event) => event.stopPropagation()}
            >
                <div className={styles.partOptionModalHeader}>
                    <div className={styles.partOptionModalTitleWrap}>
                        <div className={styles.partOptionModalIcon}>
                            {option.iconUrl ? (
                                <img
                                    src={option.iconUrl}
                                    alt={option.label}
                                    className={styles.partOptionModalIconImg}
                                />
                            ) : (
                                <img
                                    src={partIconFallbackImg}
                                    alt=""
                                    className={styles.partIconFallbackImg}
                                />
                            )}
                        </div>
                        <div>
                            <h4 className={styles.partOptionModalTitle}>{option.label}</h4>
                        </div>
                    </div>
                    <button
                        type="button"
                        className={styles.partOptionModalClose}
                        onClick={() => onClose?.()}
                        aria-label="Cerrar"
                    >
                        ×
                    </button>
                </div>

                <PartOptionConfigBlock
                    option={option}
                    onClose={onClose}
                    onSelectActivePart={onSelectActivePart}
                    onTogglePart={onTogglePart}
                    onSelectMaterial={onSelectMaterial}
                    onFetchColors={onFetchColors}
                    onTogglePalette={onTogglePalette}
                    onSelectColor={onSelectColor}
                    onClearColor={onClearColor}
                    isDualPaintEnabled={isDualPaintEnabled}
                    onToggleDualPaint={onToggleDualPaint}
                    isRoot={true}
                />
            </div>
        </div>
    );
};

const PartOptionConfigBlock = ({
    option,
    onClose,
    onSelectActivePart,
    onTogglePart,
    onSelectMaterial,
    onFetchColors,
    onTogglePalette,
    onSelectColor,
    onClearColor,
    isDualPaintEnabled,
    onToggleDualPaint,
    isRoot = false,
}) => {
    const hasMaterials = (option?.materialOptions || []).length > 0;
    const isAutoMaterialResolving = !!option?.isAutoMaterialResolving;
    const dependentOptions = option?.dependentOptions || [];
    const hasDependents = dependentOptions.length > 0;
    const hasConfigurableDependents = dependentOptions.some((childOption) => !!childOption?.hasConfigurator);
    const showSelfMaterialConfig = Boolean(
        hasMaterials
        && !hasConfigurableDependents
    );
    const canConfigure = showSelfMaterialConfig || hasDependents;
    const showMissingColorWarning = shouldShowMissingColorWarning(option);
    const canDualPaintOnOption = !!(option?.isLeftRightPart && option?.pairedPartKey);
    const dualPaintEnabledOnOption = canDualPaintOnOption ? !!isDualPaintEnabled?.(option) : false;
    const mirrorPartKey = dualPaintEnabledOnOption ? option?.pairedPartKey || null : null;
    const parentDualControllerOption = (dependentOptions || []).find(
        (childOption) => !!(childOption?.isLeftRightPart && childOption?.pairedPartKey && childOption?.pairGroupKey)
    ) || null;
    const canDualPaintOnParent = !!parentDualControllerOption;
    const dualPaintEnabledOnParent = canDualPaintOnParent
        ? !!isDualPaintEnabled?.(parentDualControllerOption)
        : false;

    useEffect(() => {
        if (!option?.selected) return;
        if (!showSelfMaterialConfig) return;
        if (!option?.selectedMaterialId) return;
        if (option.paletteLoaded || option.paletteLoading) return;
        onFetchColors?.(option.key, option.selectedMaterialId);
    }, [
        option?.key,
        option?.selected,
        showSelfMaterialConfig,
        option?.selectedMaterialId,
        option?.paletteLoaded,
        option?.paletteLoading,
        onFetchColors,
    ]);

    if (!option) return null;

    if (!canConfigure) {
        return (
            <div className={styles.emptyState}>
                Esta parte no tiene materiales ni dependencias configurables.
            </div>
        );
    }

    return (
        <div className={styles.gridGap10}>
            {showSelfMaterialConfig && !isAutoMaterialResolving ? (
                <>
                    {!option.selectedMaterialId ? (
                        null
                    ) : option.paletteLoading ? (
                        <div className={styles.noPrintsMessage}>Cargando colores compatibles...</div>
                    ) : option.availableColors.length > 0 ? (
                        <div
                            className={`${styles.partOptionPaletteSection} ${
                                showMissingColorWarning ? styles.partOptionPaletteSectionWarning : ""
                            }`}
                        >
                            {showMissingColorWarning ? (
                                <span
                                    className={styles.partOptionPaletteWarning}
                                    aria-label="Falta seleccionar un color"
                                    title="Falta seleccionar un color"
                                >
                                    <ColorWarningIcon />
                                </span>
                            ) : null}
                            <div className={styles.partOptionPaletteHeader}>
                                <div className={styles.inputLabel}>Color</div>
                                {option.selectedColor ? (
                                    <div className={styles.partOptionSelectedColor}>
                                        <span
                                            className={styles.partOptionSelectedColorSwatch}
                                            style={{
                                                background: option.selectedColor.code || "linear-gradient(45deg, #eaeaea, #cdcdcd)",
                                            }}
                                        />
                                        <span>{sanitizeColorName(option.selectedColor.name, "Color elegido")}</span>
                                    </div>
                                ) : null}
                            </div>
                            <div
                                className={`${styles.partOptionPaletteGrid} ${styles.paletteSwipeContainer} ${
                                    option.paletteOpen ? styles.paletteSwipeExpanded : styles.paletteSwipeCollapsed
                                }`}
                            >
                                {option.availableColors.map((color) => (
                                    <button
                                        key={`modal-color-${option.key}-${color.id}`}
                                        type="button"
                                        className={`${styles.colorSwatch} ${
                                            (option.selectedColor?.id ?? option.selectedColor?.color_id) === color.id
                                                ? styles.colorSwatchSelected
                                                : ""
                                        }`}
                                        style={{
                                            background: color.code || "linear-gradient(45deg, #eaeaea, #cdcdcd)",
                                        }}
                                        onClick={() => {
                                            onSelectColor?.(option.key, option.selectedMaterialId, color, { mirrorPartKey });
                                        }}
                                        aria-label={`Seleccionar ${sanitizeColorName(color.name, "color")}`}
                                        title={`${sanitizeColorName(color.name, "Color")} (${color.code || "sin código"})`}
                                    />
                                ))}
                                {!option.paletteOpen && option.availableColors.length > PALETTE_PREVIEW_LIMIT ? (
                                    <span className={styles.paletteCollapsedHint}>
                                        +{option.availableColors.length - PALETTE_PREVIEW_LIMIT}
                                    </span>
                                ) : null}
                            </div>
                            <div className={styles.paletteChevronRow}>
                                <button
                                    type="button"
                                    className={styles.paletteChevronBtn}
                                    onClick={() => {
                                        const materialId = option.selectedMaterialId;
                                        if (!materialId) return;
                                        if (!option.paletteOpen && !option.paletteLoaded) {
                                            onFetchColors?.(option.key, materialId);
                                        }
                                        onTogglePalette?.(option.key, materialId);
                                    }}
                                    aria-label={option.paletteOpen ? "Ocultar paleta de colores" : "Mostrar paleta de colores"}
                                    title={option.paletteOpen ? "Ocultar paleta" : "Mostrar paleta"}
                                >
                                    <PaletteChevronIcon expanded={!!option.paletteOpen} />
                                </button>
                            </div>
                            {option.selectedColor ? (
                                <div className={styles.partOptionPaletteActions}>
                                    <button
                                        type="button"
                                        className={styles.btn}
                                        onClick={() =>
                                            onClearColor?.(option.key, option.selectedMaterialId, { mirrorPartKey })
                                        }
                                    >
                                        Quitar color
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    ) : (
                        <div className={styles.emptyState}>
                            No hay colores compatibles para el material seleccionado.
                        </div>
                    )}
                </>
            ) : null}

            {hasDependents ? (
                <div className={styles.dependentOptionsSection}>
                    {canDualPaintOnParent ? (
                        <div className={styles.sublimationRow}>
                            <span className={styles.sublimationLabel}>Pintado dual</span>
                            <label className={styles.switch}>
                                <input
                                    type="checkbox"
                                    checked={dualPaintEnabledOnParent}
                                    onChange={(event) =>
                                        onToggleDualPaint?.(parentDualControllerOption, event.target.checked)
                                    }
                                />
                                <span className={styles.slider}></span>
                            </label>
                            <span className={styles.sublimationHint}>
                                {dualPaintEnabledOnParent
                                    ? "Ambos lados se pintan juntos"
                                    : "Cada lado se pinta por separado"}
                            </span>
                        </div>
                    ) : null}
                    <div className={styles.dependentOptionsList}>
                        {dependentOptions.map((childOption) => {
                            const canDeselect = childOption.selected && !childOption.required && !childOption.isExclusiveRequiredGroup;
                            const isChildConfigurable = childOption.hasConfigurator;
                            const showChildConfig = childOption.selected || childOption.required;

                            return (
                                <div
                                    key={`dependent-${option.key}-${childOption.key}`}
                                    className={`${styles.dependentOptionCard} ${showChildConfig ? styles.dependentOptionCardSelected : ""}`}
                                >
                                    <div className={styles.dependentOptionHeader}>
                                        <button
                                            type="button"
                                            className={styles.dependentOptionTrigger}
                                            onClick={() => {
                                                onSelectActivePart?.(childOption.key);
                                                if (!childOption.selected) {
                                                    onTogglePart?.(childOption.key, true);
                                                }
                                            }}
                                        >
                                            <span className={styles.dependentOptionIcon}>
                                                {childOption.iconUrl ? (
                                                    <img
                                                        src={childOption.iconUrl}
                                                        alt={childOption.label}
                                                        className={styles.dependentOptionIconImg}
                                                    />
                                                ) : (
                                                    <img
                                                        src={partIconFallbackImg}
                                                        alt=""
                                                        className={styles.dependentOptionIconImg}
                                                    />
                                                )}
                                            </span>
                                            <span className={styles.dependentOptionLabelWrap}>
                                                <span className={styles.dependentOptionLabel}>{childOption.label}</span>
                                                <span className={styles.dependentOptionMeta}>
                                                    {childOption.selected
                                                        ? (isChildConfigurable ? "Seleccionada y configurable" : "Seleccionada")
                                                        : (isChildConfigurable ? "Seleccionar para configurar" : "Seleccionar")}
                                                </span>
                                            </span>
                                        </button>

                                        {canDeselect ? (
                                            <button
                                                type="button"
                                                className={styles.dependentOptionDeleteBtn}
                                                onClick={() => onTogglePart?.(childOption.key, false)}
                                                aria-label={`Deseleccionar ${childOption.label}`}
                                                title={`Deseleccionar ${childOption.label}`}
                                            >
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    className={styles.partIconDeleteSvg}
                                                    aria-hidden="true"
                                                >
                                                    <path
                                                        d="M9 3h6l1 2h4v2H4V5h4l1-2Zm1 6h2v8h-2V9Zm4 0h2v8h-2V9ZM7 9h2v8H7V9Zm1 11h8a2 2 0 0 0 2-2V8H6v10a2 2 0 0 0 2 2Z"
                                                        fill="currentColor"
                                                    />
                                                </svg>
                                            </button>
                                        ) : null}
                                    </div>

                                    {showChildConfig && isChildConfigurable ? (
                                        <div className={styles.dependentOptionConfig}>
                                            <PartOptionConfigBlock
                                                option={childOption}
                                                onClose={onClose}
                                                onSelectActivePart={onSelectActivePart}
                                                onTogglePart={onTogglePart}
                                                onSelectMaterial={onSelectMaterial}
                                                onFetchColors={onFetchColors}
                                                onTogglePalette={onTogglePalette}
                                                onSelectColor={onSelectColor}
                                                onClearColor={onClearColor}
                                                isDualPaintEnabled={isDualPaintEnabled}
                                                onToggleDualPaint={onToggleDualPaint}
                                            />
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : null}

            {!isRoot && !hasMaterials && !hasDependents ? (
                <div className={styles.noPrintsMessage}>
                    Esta parte se puede seleccionar o quitar sin configuración adicional.
                </div>
            ) : null}
        </div>
    );
};

export const StripeWizardControls = ({
    stripeWizard,
    stripeDrawEditor,
    onSetStripeDrawEditor,
    activeStrokeCount = 0,
    onUndo,
    onClear,
    viewSide = "front",
}) => {
    const [isStripePaletteOpen, setIsStripePaletteOpen] = useState(false);

    useEffect(() => {
        if (stripeDrawEditor?.tool !== "move") return;
        onSetStripeDrawEditor?.((prev) => ({ ...(prev || {}), tool: "line", enabled: true }));
    }, [stripeDrawEditor?.tool, onSetStripeDrawEditor]);

    if (!stripeWizard) return null;

    const stripeColors = Array.isArray(stripeWizard.colors) ? stripeWizard.colors : [];
    const stripeHiddenColorCount = Math.max(0, stripeColors.length - STRIPE_PALETTE_PREVIEW_LIMIT);
    const visibleStripeColors = isStripePaletteOpen
        ? stripeColors
        : stripeColors.slice(0, STRIPE_PALETTE_PREVIEW_LIMIT);
    const activeStripeColorCode = String(stripeDrawEditor?.color || "").toLowerCase();
    const activeStripeColorNameRaw = stripeColors.find(
        (color) => String(color?.code || "").toLowerCase() === activeStripeColorCode
    )?.name;
    const activeStripeColorName = normalizeColorLabelWithoutNumbers(
        sanitizeColorName(activeStripeColorNameRaw, "Color elegido")
    );

    return (
        <div className={styles.gridGap12}>
            <div className={styles.inputGroup}>
                <label className={styles.inputLabel}>Color único</label>
                <div className={`${styles.colorRow} ${styles.paletteSwipeContainer} ${isStripePaletteOpen ? styles.paletteSwipeExpanded : styles.stripePaletteCollapsed}`}>
                    {visibleStripeColors.map((color) => (
                        <button
                            key={`stripe-color-${color.id}`}
                            type="button"
                            className={`${styles.colorDot} ${String(stripeDrawEditor?.color || "").toLowerCase() === String(color.code || "").toLowerCase() ? styles.selected : ""}`}
                            style={{ background: color.code || "linear-gradient(45deg,#efefef,#d8d8d8)" }}
                            onClick={() => onSetStripeDrawEditor?.((prev) => ({ ...(prev || {}), color: String(color.code || "#1d4ed8") }))}
                            title={sanitizeColorName(color.name, "Color")}
                        />
                    ))}
                    {!isStripePaletteOpen && stripeHiddenColorCount > 0 ? (
                        <span className={styles.paletteCollapsedHint}>+{stripeHiddenColorCount}</span>
                    ) : null}
                </div>
                {stripeColors.length > STRIPE_PALETTE_PREVIEW_LIMIT ? (
                    <div className={styles.paletteChevronRow}>
                        <button
                            type="button"
                            className={styles.paletteChevronBtn}
                            onClick={() => setIsStripePaletteOpen((prev) => !prev)}
                            aria-label={isStripePaletteOpen ? "Ocultar paleta de colores" : "Mostrar paleta de colores"}
                            title={isStripePaletteOpen ? "Ocultar paleta" : "Mostrar paleta"}
                        >
                            <PaletteChevronIcon expanded={isStripePaletteOpen} />
                        </button>
                    </div>
                ) : null}
            </div>
            <div className={styles.stripePreviewRow}>
                <div className={styles.stripePreviewCard}>
                    <span className={styles.stripePreviewLabel}>Color activo</span>
                    <span className={styles.stripePreviewColorRow}>
                        <span
                            className={styles.stripePreviewSwatch}
                            style={{ background: stripeDrawEditor?.color || "#1d4ed8" }}
                            aria-hidden="true"
                        />
                        <span className={styles.partOptionSelectedColor} title={activeStripeColorName || "Color elegido"}>
                            {activeStripeColorName || "Color elegido"}
                        </span>
                    </span>
                </div>
                <div className={styles.stripePreviewCard}>
                    <span className={styles.stripePreviewLabel}>Herramienta</span>
                    <div className={`${styles.stripeToolRow} ${styles.stripeToolRowCompact}`}>
                        <button
                            type="button"
                            className={`${styles.stripeToolBtn} ${!stripeDrawEditor?.enabled || stripeDrawEditor?.tool === "none" ? styles.stripeToolBtnActive : ""}`}
                            onClick={() => onSetStripeDrawEditor?.((prev) => ({ ...(prev || {}), tool: "none", enabled: false }))}
                        >
                            <i className="bi bi-slash-circle" aria-hidden="true" />
                            <span>Off</span>
                        </button>
                        <button
                            type="button"
                            className={`${styles.stripeToolBtn} ${stripeDrawEditor?.enabled && stripeDrawEditor?.tool === "line" ? styles.stripeToolBtnActive : ""}`}
                            onClick={() => onSetStripeDrawEditor?.((prev) => ({ ...(prev || {}), tool: "line", enabled: true }))}
                        >
                            <i className="bi bi-slash-lg" aria-hidden="true" />
                            <span>Recta</span>
                        </button>
                        <button
                            type="button"
                            className={`${styles.stripeToolBtn} ${stripeDrawEditor?.enabled && stripeDrawEditor?.tool === "curve" ? styles.stripeToolBtnActive : ""}`}
                            onClick={() => onSetStripeDrawEditor?.((prev) => ({ ...(prev || {}), tool: "curve", enabled: true }))}
                        >
                            <i className="bi bi-bezier2" aria-hidden="true" />
                            <span>Curva</span>
                        </button>
                        <button
                            type="button"
                            className={`${styles.stripeToolBtn} ${stripeDrawEditor?.enabled && stripeDrawEditor?.tool === "erase" ? styles.stripeToolBtnActive : ""}`}
                            onClick={() => onSetStripeDrawEditor?.((prev) => ({ ...(prev || {}), tool: "erase", enabled: true }))}
                        >
                            <i className="bi bi-eraser-fill" aria-hidden="true" />
                            <span>Borrar</span>
                        </button>
                    </div>
                </div>
            </div>
            <div className={styles.hint}>
                Vista actual: <strong>{viewSide === "back" ? "Espalda" : "Frente"}</strong> · Trazos: <strong>{activeStrokeCount}</strong>
            </div>
            <div className={styles.stripeInlineActions}>
                <button type="button" className={styles.btn} onClick={onUndo} disabled={!activeStrokeCount}>
                    Deshacer último
                </button>
                <button type="button" className={styles.btn} onClick={onClear} disabled={!activeStrokeCount}>
                    Limpiar
                </button>
            </div>
        </div>
    );
};

const CustomCutWizardControls = ({
    customCutWizard,
    customCutEditor,
    onSetCustomCutEditor,
    customCutZones = [],
    onClearZoneColor,
    activeCutCount = 0,
    onUndo,
    onClear,
}) => {
    const [activeZoneId, setActiveZoneId] = useState("");
    const zones = useMemo(
        () => (Array.isArray(customCutZones) ? customCutZones : []),
        [customCutZones]
    );
    useEffect(() => {
        if (!zones.length) {
            setActiveZoneId("");
            return;
        }
        if (zones.some((zone) => String(zone.id) === String(activeZoneId))) return;
        setActiveZoneId(String(zones[0].id));
    }, [zones, activeZoneId]);

    if (!customCutWizard?.available) {
        // Modo propuesto: recortes de Little Blue (no editables). Ofrecer el opt-in.
        if (customCutWizard?.canOptIn) {
            return (
                <div className={styles.gridGap12}>
                    <div className={styles.customCutToolbarCard}>
                        <div className={styles.warningNotice}>
                            Este modelo ya trae sus recortes definidos por Little Blue. ¿Querés agregar un recorte extra?
                        </div>
                        <button
                            type="button"
                            className={styles.btn}
                            onClick={() => customCutWizard.onOptIn?.()}
                        >
                            Agregar recorte extra
                        </button>
                    </div>
                </div>
            );
        }
        return null;
    }

    return (
        <div className={styles.gridGap12}>
            <div className={styles.customCutToolbarCard}>
                <div className={styles.customCutToolbarHeader}>
                    <span className={styles.stripePreviewLabel}>Herramienta</span>
                </div>
                <div className={styles.warningNotice}>
                    Si querés pintar una parte por encima de un recorte ya pintado, buscá y pintá esa parte puntual.
                </div>
                <div className={styles.customCutToolbarScroller}>
                    <div className={styles.customCutToolbarRow}>
                        <button
                            type="button"
                            className={`${styles.stripeToolBtn} ${!customCutEditor?.enabled || customCutEditor?.tool === "none" ? styles.stripeToolBtnActive : ""}`}
                            onClick={() => onSetCustomCutEditor?.((prev) => ({ ...(prev || {}), tool: "none", enabled: false }))}
                        >
                            <i className="bi bi-scissors" aria-hidden="true" />
                            <span>Off</span>
                        </button>
                        <button
                            type="button"
                            className={`${styles.stripeToolBtn} ${customCutEditor?.enabled && customCutEditor?.tool === "line" ? styles.stripeToolBtnActive : ""}`}
                            onClick={() => onSetCustomCutEditor?.((prev) => ({ ...(prev || {}), tool: "line", enabled: true }))}
                        >
                            <i className="bi bi-slash-lg" aria-hidden="true" />
                            <span>Recta</span>
                        </button>
                        <button
                            type="button"
                            className={`${styles.stripeToolBtn} ${customCutEditor?.enabled && customCutEditor?.tool === "curve" ? styles.stripeToolBtnActive : ""}`}
                            onClick={() => onSetCustomCutEditor?.((prev) => ({ ...(prev || {}), tool: "curve", enabled: true }))}
                        >
                            <i className="bi bi-bezier2" aria-hidden="true" />
                            <span>Curva</span>
                        </button>
                        <button
                            type="button"
                            className={`${styles.stripeToolBtn} ${customCutEditor?.enabled && customCutEditor?.tool === "erase" ? styles.stripeToolBtnActive : ""}`}
                            onClick={() => onSetCustomCutEditor?.((prev) => ({ ...(prev || {}), tool: "erase", enabled: true }))}
                        >
                            <i className="bi bi-eraser-fill" aria-hidden="true" />
                            <span>Borrar</span>
                        </button>
                    </div>
                </div>
                <div className={styles.customCutActionsScroller}>
                    <div className={styles.customCutActionsRow}>
                        <button type="button" className={styles.btn} onClick={onUndo} disabled={!activeCutCount}>
                            Deshacer último
                        </button>
                        <button type="button" className={styles.btn} onClick={onClear} disabled={!activeCutCount}>
                            Limpiar recortes
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const PartMaterialView = ({
    partCards = [],
    partGroups = [],
    activePartKey = null,
    activeSectionKey: controlledActiveSectionKey = "",
    onChangeActiveSection = null,
    onSelectActivePart,
    onTogglePart,
    // Las tiras se configuran ahora en su propio paso; los recortes libres salieron del flujo del
    // cliente. El paso de pintado no inyecta esas secciones sintéticas (includeStripeSections=false).
    includeStripeSections = false,
    hideSearch = false,
    hotspotFilter = null,
    onClearHotspotFilter,
    onSelectMaterial,
    onFetchColors,
    onTogglePalette,
    onSelectColor,
    onClearColor,
    onApplyBaseColor = null,
    baseColor = null,
    baseColorOptions = [],
    isDualPaintEnabled,
    onToggleDualPaint,
    garmentDefaultMaterialName = "",
    stripeWizard = null,
    customCutWizard = null,
    customCutEditor = null,
    onSetCustomCutEditor = null,
    customCutsBySide = null,
    customCutZones = null,
    customCutZoneColors = null,
    onSelectCustomCutZoneColor = null,
    onClearCustomCutZoneColor = null,
    onUndoCustomCut = null,
    onClearCustomCuts = null,
    customCutSummary = null,
    stripeDrawEditor = null,
    onSetStripeDrawEditor = null,
    stripeDrawingsBySide = null,
    viewSide = "front",
    onUndoStripeStroke = null,
    onClearStripeDrawings = null,
}) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [openOptionKey, setOpenOptionKey] = useState(null);
    const [collapsedSections, setCollapsedSections] = useState({});
    // UX 2026-07: "Finalizar colores". Mientras pinta, las PARTES quedan arriba; al finalizar,
    // suben los pendientes (Extras → Tiras → Recortes) y las partes bajan (pedido del dueño).
    const [coloresFinalizados, setColoresFinalizados] = useState(false);
    const [isStripeSectionCollapsed, setIsStripeSectionCollapsed] = useState(true);
    const [isStripeTutorialOpen, setIsStripeTutorialOpen] = useState(false);
    const [activeSectionKeyInternal, setActiveSectionKeyInternal] = useState("");
    const [sectionFadeKey, setSectionFadeKey] = useState(0);
    const [canScrollSectionTabsLeft, setCanScrollSectionTabsLeft] = useState(false);
    const [canScrollSectionTabsRight, setCanScrollSectionTabsRight] = useState(false);
    const sectionTabsViewportRef = useRef(null);
    const sectionTabsTouchStateRef = useRef({
        touchId: null,
        startX: 0,
        startY: 0,
        startScrollLeft: 0,
        dragging: false,
        suppressClick: false,
    });
    const pendingAutoAdvanceGroupKeyRef = useRef("");
    const normalizedSearch = useMemo(() => normalizeSearchText(searchTerm), [searchTerm]);
    const activeSectionKey = controlledActiveSectionKey || activeSectionKeyInternal;
    const updateActiveSectionKey = useCallback((nextKey, options = {}) => {
        const normalized = String(nextKey || "");
        if (!normalized) return;
        if (controlledActiveSectionKey) {
            onChangeActiveSection?.(normalized);
        } else {
            setActiveSectionKeyInternal(normalized);
            onChangeActiveSection?.(normalized);
        }
        if (options.animate) {
            setSectionFadeKey((prev) => prev + 1);
        }
    }, [controlledActiveSectionKey, onChangeActiveSection]);
    const getGroupColorTargetEntries = useCallback((group) => {
        if (!group || group.syntheticStripe || group.syntheticCuts) return [];
        const options = Array.isArray(group.options) ? group.options : [];
        if (!options.length) return [];
        const seen = new Set();
        return options.flatMap((option) =>
            collectColorTargetOptions(option).filter((entry) => {
                const key = String(entry?.key || "");
                if (!key || seen.has(key)) return false;
                seen.add(key);
                return true;
            })
        );
    }, []);
    const filteredPartGroups = useMemo(() => {
        if (!normalizedSearch) return partGroups;
        return partGroups.filter((group) => {
            const groupName = normalizeSearchText(group?.label);
            const optionMatch = (group?.options || []).some((option) =>
                normalizeSearchText(option?.label).includes(normalizedSearch)
            );
            return groupName.includes(normalizedSearch) || optionMatch;
        });
    }, [partGroups, normalizedSearch]);
    const optionLookup = useMemo(() => {
        const lookup = {};
        partGroups.forEach((group) => {
            (group.options || []).forEach((option) => {
                lookup[String(option.key)] = { option, group };
            });
        });
        return lookup;
    }, [partGroups]);
    const openOption = openOptionKey ? optionLookup[String(openOptionKey)]?.option || null : null;
    const renderedPartGroups = useMemo(() => {
        const isExtrasGroup = (group) => {
            const sourceKey = String(group?.sourceKey || "").toLowerCase();
            const groupKey = String(group?.key || "").toLowerCase();
            const groupLabel = normalizeSearchText(group?.label || "");
            return sourceKey === "extras" || groupKey === "group-extras" || groupLabel === "extras";
        };
        // Reordena cuando el dueño toca "Finalizar colores": los pendientes suben, las partes bajan.
        // Orden pedido: Extras → Tiras → Recortes → Partes.
        const applyFinalOrder = (list) => {
            if (!coloresFinalizados) return list;
            const extras = list.filter((g) => isExtrasGroup(g));
            const tiras = list.filter((g) => g?.syntheticStripe);
            const recortes = list.filter((g) => g?.syntheticCuts);
            const partes = list.filter((g) => !isExtrasGroup(g) && !g?.syntheticStripe && !g?.syntheticCuts);
            return [...extras, ...tiras, ...recortes, ...partes];
        };
        // El paso de pintado ya no muestra tiras ni recortes: viven en su propio paso (tiras) y los
        // recortes libres salieron del flujo del cliente. Solo se inyectan si se habilita explícitamente.
        const wantStripe = includeStripeSections && stripeWizard?.available;
        if (!wantStripe) return applyFinalOrder(filteredPartGroups);
        const syntheticSections = [{
            key: "group-stripes",
            label: "Tiras",
            syntheticStripe: true,
        }];
        const extrasIndex = filteredPartGroups.findIndex((group) => isExtrasGroup(group));
        if (extrasIndex < 0) {
            return applyFinalOrder([...filteredPartGroups, ...syntheticSections]);
        }
        return applyFinalOrder([
            ...filteredPartGroups.slice(0, extrasIndex),
            ...syntheticSections,
            ...filteredPartGroups.slice(extrasIndex),
        ]);
    }, [filteredPartGroups, includeStripeSections, stripeWizard, coloresFinalizados]);
    const orderedAutoAdvanceSections = useMemo(
        () =>
            (renderedPartGroups || [])
                .map((group, sectionIndex) => {
                    if (group.syntheticStripe || group.syntheticCuts) {
                        return {
                            ...group,
                            autoAdvanceSectionIndex: sectionIndex + 1,
                            autoAdvanceTargets: [],
                        };
                    }
                    const targets = getGroupColorTargetEntries(group).map((entry, targetIndex) => ({
                        ...entry,
                        autoAdvanceTargetIndex: targetIndex + 1,
                    }));
                    return {
                        ...group,
                        autoAdvanceSectionIndex: sectionIndex + 1,
                        autoAdvanceTargets: targets,
                    };
                }),
        [renderedPartGroups, getGroupColorTargetEntries]
    );
    const isRegularGroupComplete = useCallback((group) => {
        if (!group || group.syntheticStripe || group.syntheticCuts) return false;
        const resolvedGroup = orderedAutoAdvanceSections.find(
            (candidate) => String(candidate.key) === String(group.key)
        ) || group;
        const targetEntries = resolvedGroup.autoAdvanceTargets || getGroupColorTargetEntries(resolvedGroup);
        if (!targetEntries.length) return false;
        return targetEntries.every((entry) => entry?.colorId !== null && entry?.colorId !== undefined);
    }, [orderedAutoAdvanceSections, getGroupColorTargetEntries]);
    const visiblePartGroups = useMemo(() => {
        if (!renderedPartGroups.length) return [];
        if (!activeSectionKey) return [renderedPartGroups[0]];
        const active = renderedPartGroups.find((group) => String(group.key) === String(activeSectionKey));
        return active ? [active] : [renderedPartGroups[0]];
    }, [renderedPartGroups, activeSectionKey]);
    const handleSectionTabsWheel = (event) => {
        const viewport = event.currentTarget;
        if (!viewport) return;
        if (viewport.scrollWidth <= viewport.clientWidth + 1) return;

        const dominantDelta = Math.abs(event.deltaX) > Math.abs(event.deltaY)
            ? event.deltaX
            : event.deltaY;
        if (!dominantDelta) return;

        const deltaMultiplier = event.deltaMode === 1 ? 16 : 1;
        const scrollDelta = dominantDelta * deltaMultiplier;
        const movingForward = scrollDelta > 0;
        const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
        const atStart = viewport.scrollLeft <= 0;
        const atEnd = viewport.scrollLeft >= (maxScrollLeft - 1);

        if ((movingForward && atEnd) || (!movingForward && atStart)) {
            return;
        }

        event.preventDefault();
        viewport.scrollLeft += scrollDelta;
    };

    const refreshSectionTabsScrollState = useCallback(() => {
        const viewport = sectionTabsViewportRef.current;
        if (!viewport) {
            setCanScrollSectionTabsLeft(false);
            setCanScrollSectionTabsRight(false);
            return;
        }
        const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
        if (maxScrollLeft <= 1) {
            setCanScrollSectionTabsLeft(false);
            setCanScrollSectionTabsRight(false);
            return;
        }
        setCanScrollSectionTabsLeft(viewport.scrollLeft > 1);
        setCanScrollSectionTabsRight(viewport.scrollLeft < (maxScrollLeft - 1));
    }, []);

    const scrollSectionTabs = useCallback((direction = "right") => {
        const viewport = sectionTabsViewportRef.current;
        if (!viewport) return;
        const delta = Math.max(120, Math.round(viewport.clientWidth * 0.72));
        const movement = direction === "left" ? -delta : delta;
        viewport.scrollBy({
            left: movement,
            behavior: "smooth",
        });
    }, []);

    const handleSectionTabsTouchStart = useCallback((event) => {
        const viewport = sectionTabsViewportRef.current;
        const touch = event.touches?.[0];
        if (!viewport || !touch) return;
        sectionTabsTouchStateRef.current = {
            touchId: touch.identifier,
            startX: touch.clientX,
            startY: touch.clientY,
            startScrollLeft: viewport.scrollLeft,
            dragging: false,
            suppressClick: false,
        };
    }, []);

    const handleSectionTabsTouchMove = useCallback((event) => {
        const viewport = sectionTabsViewportRef.current;
        if (!viewport) return;
        const state = sectionTabsTouchStateRef.current;
        const touch = Array.from(event.touches || []).find(
            (candidate) => candidate.identifier === state.touchId
        ) || event.touches?.[0];
        if (!touch) return;

        const deltaX = touch.clientX - state.startX;
        const deltaY = touch.clientY - state.startY;

        if (!state.dragging) {
            if (Math.abs(deltaX) < 6 || Math.abs(deltaX) <= Math.abs(deltaY)) {
                return;
            }
            state.dragging = true;
            state.suppressClick = true;
        }

        event.preventDefault();
        viewport.scrollLeft = state.startScrollLeft - deltaX;
        refreshSectionTabsScrollState();
    }, [refreshSectionTabsScrollState]);

    const handleSectionTabsTouchEnd = useCallback(() => {
        const state = sectionTabsTouchStateRef.current;
        if (state.dragging || state.suppressClick) {
            window.setTimeout(() => {
                sectionTabsTouchStateRef.current = {
                    touchId: null,
                    startX: 0,
                    startY: 0,
                    startScrollLeft: 0,
                    dragging: false,
                    suppressClick: false,
                };
            }, 0);
            return;
        }
        sectionTabsTouchStateRef.current = {
            touchId: null,
            startX: 0,
            startY: 0,
            startScrollLeft: 0,
            dragging: false,
            suppressClick: false,
        };
    }, []);

    const handleSectionTabsClickCapture = useCallback((event) => {
        if (!sectionTabsTouchStateRef.current.suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
    }, []);

    useEffect(() => {
        refreshSectionTabsScrollState();
    }, [refreshSectionTabsScrollState, renderedPartGroups, activeSectionKey]);

    useEffect(() => {
        const viewport = sectionTabsViewportRef.current;
        if (!viewport) return undefined;
        const onScroll = () => refreshSectionTabsScrollState();
        const onResize = () => refreshSectionTabsScrollState();
        viewport.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onResize);
        return () => {
            viewport.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onResize);
        };
    }, [refreshSectionTabsScrollState]);

    useEffect(() => {
        setCollapsedSections((prev) => {
            const next = { ...prev };
            let changed = false;
            (filteredPartGroups || []).forEach((group) => {
                const groupKey = String(group?.key || "");
                if (!groupKey) return;
                const optionsCount = (group?.options || []).length;
                const isCollapsible = optionsCount > COLLAPSIBLE_SECTION_OPTION_LIMIT;
                if (!isCollapsible) {
                    if (next[groupKey] !== undefined) {
                        delete next[groupKey];
                        changed = true;
                    }
                    return;
                }
                if (next[groupKey] === undefined) {
                    next[groupKey] = true;
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [filteredPartGroups]);

    useEffect(() => {
        if (!openOptionKey) return;
        if (optionLookup[String(openOptionKey)]) return;
        setOpenOptionKey(null);
    }, [openOptionKey, optionLookup]);

    useEffect(() => {
        if (!isStripeTutorialOpen) return undefined;
        const onKeyDown = (event) => {
            if (event.key === "Escape") setIsStripeTutorialOpen(false);
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [isStripeTutorialOpen]);

    useEffect(() => {
        if (!renderedPartGroups.length) {
            if (activeSectionKey && !controlledActiveSectionKey) setActiveSectionKeyInternal("");
            return;
        }
        const exists = renderedPartGroups.some((group) => String(group.key) === String(activeSectionKey));
        if (!exists) {
            updateActiveSectionKey(String(renderedPartGroups[0].key));
        }
    }, [renderedPartGroups, activeSectionKey, controlledActiveSectionKey, updateActiveSectionKey]);

    const advanceToNextSection = useCallback((currentGroupKey) => {
        const groups = orderedAutoAdvanceSections;
        if (!groups.length) return;
        const currentIndex = groups.findIndex((group) => String(group.key) === String(currentGroupKey));
        if (currentIndex < 0) return;
        const nextGroup = groups[currentIndex + 1];
        if (!nextGroup) return;
        updateActiveSectionKey(String(nextGroup.key), { animate: true });
        const nextOptionKey = String(nextGroup.activeOptionKey || nextGroup.options?.[0]?.key || "");
        if (nextOptionKey) {
            onSelectActivePart?.(nextOptionKey);
        }
    }, [orderedAutoAdvanceSections, updateActiveSectionKey, onSelectActivePart]);

    useEffect(() => {
        const pendingGroupKey = String(pendingAutoAdvanceGroupKeyRef.current || "");
        if (!pendingGroupKey) return;
        const refreshedGroup = orderedAutoAdvanceSections.find((group) => String(group.key) === pendingGroupKey);
        if (!refreshedGroup || !isRegularGroupComplete(refreshedGroup)) return;
        pendingAutoAdvanceGroupKeyRef.current = "";
        advanceToNextSection(pendingGroupKey);
    }, [orderedAutoAdvanceSections, isRegularGroupComplete, advanceToNextSection]);

    const handleColorApplied = useCallback((partKey, materialId, color, extra = {}) => {
        onSelectColor?.(partKey, materialId, color, extra);
        const parentGroup = orderedAutoAdvanceSections.find((group) =>
            Array.isArray(group.options) && group.options.some((option) => optionTreeContainsKey(option, partKey))
        );
        if (!parentGroup || parentGroup.syntheticStripe || parentGroup.syntheticCuts) return;
        pendingAutoAdvanceGroupKeyRef.current = String(parentGroup.key);
    }, [onSelectColor, orderedAutoAdvanceSections]);

    if (!partCards.length || !partGroups.length) {
        return (
            <section className={styles.sectionCard}>
                <div className={styles.emptyState}>No hay partes disponibles en esta prenda.</div>
            </section>
        );
    }

    return (
        <section className={styles.sectionCard}>
            {onApplyBaseColor && Array.isArray(baseColorOptions) && baseColorOptions.length ? (
                <div className={styles.hint} style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
                    <span><strong>Color base</strong> — pintá toda la prenda de un color y después corregí las partes que lleven otro.</span>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {baseColorOptions.slice(0, 40).map((c, i) => {
                            const code = c?.code || c?.hex || "#cccccc";
                            const active = baseColor && String(baseColor.code || baseColor.hex || "").toLowerCase() === String(code).toLowerCase();
                            return (
                                <button
                                    key={`base-color-${c?.id || c?.color_id || i}`}
                                    type="button"
                                    title={c?.name || code}
                                    onClick={() => onApplyBaseColor(c)}
                                    style={{
                                        width: 26,
                                        height: 26,
                                        borderRadius: "50%",
                                        background: code,
                                        border: active ? "3px solid #2f6fed" : "1px solid rgba(0,0,0,.2)",
                                        cursor: "pointer",
                                        padding: 0,
                                    }}
                                />
                            );
                        })}
                    </div>
                </div>
            ) : null}
            {garmentDefaultMaterialName ? (
                <div className={styles.hint}>
                    Material por defecto de la prenda base: <strong>{garmentDefaultMaterialName}</strong>
                </div>
            ) : null}
            {/* "Finalizar colores": al terminar de pintar, suben los pendientes (Extras/Tiras/Recortes)
                y bajan las partes, para no tener que scrollear a buscarlos. */}
            {!coloresFinalizados ? (
                <div className={styles.hint} style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 10 }}>
                    <span>👆 Tocá sobre la imagen para cambiarle el color a una parte. Cuando termines con los colores, tocá <strong>Finalizar</strong>.</span>
                    <button
                        type="button"
                        onClick={() => setColoresFinalizados(true)}
                        style={{
                            alignSelf: "flex-start",
                            background: "#2f6fed",
                            color: "#fff",
                            border: "none",
                            borderRadius: 999,
                            padding: "8px 18px",
                            fontWeight: 700,
                            cursor: "pointer",
                        }}
                    >
                        Finalizar colores ✓
                    </button>
                </div>
            ) : (
                <div className={styles.hint} style={{ marginBottom: 10 }}>
                    <button
                        type="button"
                        onClick={() => setColoresFinalizados(false)}
                        style={{ background: "none", border: "none", color: "#2f6fed", fontWeight: 600, cursor: "pointer", padding: 0 }}
                    >
                        ← Volver a editar colores
                    </button>
                </div>
            )}
            <div className={styles.partsTopControls}>
                {/* UX 2026-07-05: las pestañas horizontales se reemplazan por el checklist
                    vertical de decisiones (abajo). El bloque queda apagado, no borrado. */}
                {false && renderedPartGroups.length > 0 ? (
                    <div className={`${styles.sectionTabsShell} ${styles.partsSectionTabsBlock}`}>
                        <div className={styles.sectionTabsArrowLane}>
                            <button
                                type="button"
                                className={`${styles.sectionTabsArrowBtn} ${styles.sectionTabsArrowBtnLeft}`}
                                onClick={() => scrollSectionTabs("left")}
                                aria-label="Mover secciones a la izquierda"
                                title="Mover secciones a la izquierda"
                                disabled={!canScrollSectionTabsLeft}
                            >
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M14.5 6L8.5 12L14.5 18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        </div>
                        <div
                            ref={sectionTabsViewportRef}
                            className={styles.sectionTabsViewport}
                            role="tablist"
                            aria-label="Secciones de partes"
                            onWheel={handleSectionTabsWheel}
                            onTouchStart={handleSectionTabsTouchStart}
                            onTouchMove={handleSectionTabsTouchMove}
                            onTouchEnd={handleSectionTabsTouchEnd}
                            onTouchCancel={handleSectionTabsTouchEnd}
                            onClickCapture={handleSectionTabsClickCapture}
                        >
                            <div className={styles.sectionTabsTrack}>
                                {renderedPartGroups.map((group) => {
                                    const groupKey = String(group.key);
                                    const isActive = groupKey === String(activeSectionKey || renderedPartGroups[0]?.key || "");
                                    return (
                                        <button
                                            key={`section-tab-${groupKey}`}
                                            type="button"
                                            role="tab"
                                            aria-selected={isActive ? "true" : "false"}
                                            className={`${styles.sectionTabBtn} ${isActive ? styles.sectionTabBtnActive : ""}`}
                                            onClick={() => {
                                                updateActiveSectionKey(groupKey);
                                                if (!group.syntheticStripe && !group.syntheticCuts && group.activeOptionKey) {
                                                    onSelectActivePart?.(group.activeOptionKey);
                                                }
                                            }}
                                    >
                                            <span className={styles.sectionTabLabel}>
                                                <span className={styles.sectionTabDot} aria-hidden="true" />
                                                {group.label}
                                            </span>
                                            {!group.syntheticStripe && !group.syntheticCuts && group.options?.length ? (
                                                <span className={styles.sectionTabCount}>{group.options.length}</span>
                                            ) : null}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div className={styles.sectionTabsArrowLane}>
                            <button
                                type="button"
                                className={`${styles.sectionTabsArrowBtn} ${styles.sectionTabsArrowBtnRight}`}
                                onClick={() => scrollSectionTabs("right")}
                                aria-label="Mover secciones a la derecha"
                                title="Mover secciones a la derecha"
                                disabled={!canScrollSectionTabsRight}
                            >
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M9.5 6L15.5 12L9.5 18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        </div>
                    </div>
                ) : null}
                {renderedPartGroups.length > 0 ? (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", margin: "6px 0 2px" }}>
                        <span style={{ fontWeight: 800, fontSize: 15, color: "#0f172a" }}>Tus decisiones</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#6366f1" }}>
                            {(() => {
                                const regulares = renderedPartGroups.filter((g) => !g.syntheticStripe && !g.syntheticCuts);
                                const listas = regulares.filter((g) => isRegularGroupComplete(g));
                                return `${listas.length} de ${regulares.length} listas`;
                            })()}
                        </span>
                    </div>
                ) : null}
                {!hideSearch ? (
                <div className={`${styles.inputGroup} ${styles.partsSearchBlock}`}>
                    <label className={styles.inputLabel}>Buscador de partes</label>
                    <div className={styles.searchPartsRow}>
                        <span className={styles.searchIcon} aria-hidden="true">
                            <svg viewBox="0 0 24 24" className={styles.searchIconSvg}>
                                <path
                                    d="M10.5 3a7.5 7.5 0 1 0 4.73 13.32l4.23 4.23 1.54-1.54-4.23-4.23A7.5 7.5 0 0 0 10.5 3Zm0 2.2a5.3 5.3 0 1 1 0 10.6 5.3 5.3 0 0 1 0-10.6Z"
                                    fill="currentColor"
                                />
                            </svg>
                        </span>
                        <input
                            type="text"
                            className={styles.inputField}
                            value={searchTerm}
                            placeholder="Ej: capucha, manga, espalda..."
                            onChange={(event) => setSearchTerm(event.target.value)}
                        />
                        {searchTerm ? (
                            <button
                                type="button"
                                className={styles.searchClearBtn}
                                onClick={() => setSearchTerm("")}
                                aria-label="Limpiar búsqueda"
                                title="Limpiar búsqueda"
                            >
                                ×
                            </button>
                        ) : null}
                    </div>
                </div>
                ) : null}
            </div>

            {hotspotFilter?.partKey ? (
                <div className={styles.hotspotFilterBanner}>
                    <div className={styles.hotspotFilterText}>
                        {`Filtrado por hotspot ${sanitizePrintLocationLabel(hotspotFilter.displayLabel || "", "Diseño")}`}
                    </div>
                    <button
                        type="button"
                        className={styles.hotspotFilterClearBtn}
                        onClick={() => onClearHotspotFilter?.()}
                    >
                        Ver todas
                    </button>
                </div>
            ) : null}

            <div key={`section-fade-${sectionFadeKey}`} className={`${styles.partGroupList} ${styles.sectionAutoAdvanceFade}`}>
                {renderedPartGroups.map((group) => {
                    const resolvedOrderedGroup = orderedAutoAdvanceSections.find(
                        (candidate) => String(candidate.key) === String(group.key)
                    );
                    // ── Checklist vertical (UX 2026-07-05, pedido del dueño): una fila por
                    // decisión con estado (✓ lista · ! falta · ○ opcional) y resumen de lo
                    // elegido; tocar la fila la abre (accordion). Registro de qué se hizo y qué falta.
                    const isActiveGroup = visiblePartGroups.some((v) => String(v.key) === String(group.key));
                    const esOpcional = group.syntheticStripe || group.syntheticCuts;
                    const completa = !esOpcional && isRegularGroupComplete(group);
                    const targetsResumen = esOpcional ? [] : getGroupColorTargetEntries(group);
                    const conColor = targetsResumen.filter((t) => t?.colorId !== null && t?.colorId !== undefined).length;
                    const activeOpt = !esOpcional
                        ? (group.options || []).find((o) => String(o.key) === String(group.activeOptionKey))
                        : null;
                    const resumenFila = esOpcional
                        ? "opcional — tocá para ver"
                        : ([activeOpt?.label, targetsResumen.length ? `${conColor}/${targetsResumen.length} colores` : null]
                            .filter(Boolean).join(" · ") || "sin elegir todavía");
                    const estadoIcono = esOpcional ? "○" : (completa ? "✓" : "!");
                    const estadoColor = esOpcional ? "#94a3b8" : (completa ? "#059669" : "#d97706");
                    const estadoFondo = esOpcional ? "#f1f5f9" : (completa ? "#d1fae5" : "#fef3c7");
                    return (
                    <div key={`decision-${group.key}`}>
                        <button
                            type="button"
                            onClick={() => {
                                updateActiveSectionKey(String(group.key));
                                if (!esOpcional && group.activeOptionKey) {
                                    onSelectActivePart?.(group.activeOptionKey);
                                }
                            }}
                            style={{
                                display: "flex", alignItems: "center", gap: 10, width: "100%",
                                padding: "10px 12px", marginBottom: 6, textAlign: "left",
                                background: isActiveGroup ? "#eef2ff" : "#fff",
                                border: isActiveGroup ? "2px solid #6366f1" : "1px solid #e2e8f0",
                                borderRadius: 12, cursor: "pointer",
                            }}
                        >
                            <span style={{
                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                width: 26, height: 26, borderRadius: "50%", flex: "none",
                                background: estadoFondo, color: estadoColor, fontWeight: 800, fontSize: 15,
                            }}>{estadoIcono}</span>
                            <span style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ display: "block", fontWeight: 700, fontSize: 14, color: "#0f172a" }}>{group.label}</span>
                                <span style={{ display: "block", fontSize: 12, color: completa ? "#059669" : "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{resumenFila}</span>
                            </span>
                            {(() => {
                                // Muestra los colores elegidos de la parte (se va completando en vivo).
                                const dots = [];
                                const seenCodes = new Set();
                                (targetsResumen || []).forEach((t) => {
                                    const code = t?.colorCode;
                                    if (!code || seenCodes.has(code)) return;
                                    seenCodes.add(code);
                                    dots.push(code);
                                });
                                if (!dots.length) return null;
                                return (
                                    <span style={{ display: "inline-flex", gap: 3, flex: "none", marginRight: 2 }}>
                                        {dots.slice(0, 4).map((code, i) => (
                                            <span key={`dot-${group.key}-${i}`} title={code}
                                                style={{ width: 18, height: 18, borderRadius: "50%", background: code, border: "2px solid #fff", boxShadow: "0 0 0 1px #cbd5e1" }} />
                                        ))}
                                    </span>
                                );
                            })()}
                            <span style={{ color: "#94a3b8", fontSize: 18, flex: "none", transform: isActiveGroup ? "rotate(90deg)" : "none", transition: "transform .15s" }}>›</span>
                        </button>
                        {isActiveGroup ? (
                    <section
                        key={group.key}
                        className={styles.partGroupSection}
                        data-section-index={resolvedOrderedGroup?.autoAdvanceSectionIndex || ""}
                    >
                        {group.syntheticCuts ? (
                            <>
                                <div className={styles.partGroupHeader}>
                                    <div className={styles.partGroupTitleWrap}>
                                        <h5 className={styles.partGroupTitle}>Recortes personalizados</h5>
                                        <div className={styles.customCutSummaryStack}>
                                            {(customCutSummary?.details || []).length > 0 ? (
                                                customCutSummary.details.map((detail) => (
                                                    <div key={`custom-cut-summary-${detail.id}`} className={styles.customCutSummaryItem}>
                                                        <span className={styles.customCutSummaryLabel}>{detail.label}</span>
                                                        <span className={styles.customCutSummaryColors}>
                                                            {(detail.colors || []).length > 0
                                                                ? detail.colors.map((color, index) => (
                                                                    <span
                                                                        key={`custom-cut-summary-${detail.id}-color-${index}`}
                                                                        className={styles.customCutSummaryColorItem}
                                                                        title={String(color?.name || color?.code || "Color aplicado")}
                                                                    >
                                                                        <span
                                                                            className={styles.appliedPartColorDot}
                                                                            style={color?.code ? { background: color.code } : undefined}
                                                                        />
                                                                        {formatCustomCutColorSummary(color)}
                                                                    </span>
                                                                ))
                                                                : "Sin color"}
                                                        </span>
                                                    </div>
                                                ))
                                            ) : (
                                                Array.from({ length: Number(customCutSummary?.total || 0) }).map((_, index) => (
                                                    <div
                                                        key={`custom-cut-summary-fallback-${index + 1}`}
                                                        className={styles.customCutSummaryItem}
                                                    >
                                                        <span className={styles.customCutSummaryLabel}>{`Recorte ${index + 1}`}</span>
                                                        <span className={styles.customCutSummaryColors}>Sin color</span>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.sectionSwipeContainer}>
                                    <CustomCutWizardControls
                                        customCutWizard={customCutWizard}
                                        customCutEditor={customCutEditor}
                                        onSetCustomCutEditor={onSetCustomCutEditor}
                                        customCutZones={customCutZones}
                                        onSelectZoneColor={onSelectCustomCutZoneColor}
                                        onClearZoneColor={onClearCustomCutZoneColor}
                                        activeCutCount={Array.isArray(customCutsBySide?.[viewSide]) ? customCutsBySide[viewSide].length : 0}
                                        onUndo={() => onUndoCustomCut?.(viewSide)}
                                        onClear={() => onClearCustomCuts?.(viewSide)}
                                        viewSide={viewSide}
                                    />
                                </div>
                            </>
                        ) : group.syntheticStripe ? (
                            <>
                                <div className={styles.partGroupHeader}>
                                    <div className={styles.partGroupTitleWrap}>
                                        <div className={styles.stripeGroupTitleRow}>
                                            <h5 className={styles.partGroupTitle}>Tiras</h5>
                                            <button
                                                type="button"
                                                className={styles.stripeTutorialTrigger}
                                                onClick={() => setIsStripeTutorialOpen(true)}
                                                aria-label="Ver tutorial de tiras"
                                                title="Ver tutorial de tiras"
                                                data-tooltip="Cómo pintar tiras"
                                            >
                                                <i className="bi bi-question-lg" aria-hidden="true" />
                                            </button>
                                        </div>
                                        <span className={styles.partGroupActiveOption}>
                                            {`${Array.isArray(stripeDrawingsBySide?.[viewSide]) ? stripeDrawingsBySide[viewSide].length : 0} trazos`}
                                        </span>
                                    </div>
                                </div>
                                <div className={`${styles.sectionSwipeContainer} ${
                                    isStripeSectionCollapsed ? styles.sectionSwipeCollapsed : styles.sectionSwipeExpanded
                                }`}>
                                    {!isStripeSectionCollapsed ? (
                                        <StripeWizardControls
                                            stripeWizard={stripeWizard}
                                            stripeDrawEditor={stripeDrawEditor}
                                            onSetStripeDrawEditor={onSetStripeDrawEditor}
                                            activeStrokeCount={Array.isArray(stripeDrawingsBySide?.[viewSide]) ? stripeDrawingsBySide[viewSide].length : 0}
                                            onUndo={() => onUndoStripeStroke?.(viewSide)}
                                            onClear={() => onClearStripeDrawings?.(viewSide)}
                                            viewSide={viewSide}
                                        />
                                    ) : null}
                                </div>
                                <div className={styles.sectionChevronRow}>
                                    <button
                                        type="button"
                                        className={styles.sectionChevronBtn}
                                        onClick={() => setIsStripeSectionCollapsed((prev) => !prev)}
                                        aria-label={isStripeSectionCollapsed ? "Desplegar sección de tiras" : "Colapsar sección de tiras"}
                                        title={isStripeSectionCollapsed ? "Desplegar sección de tiras" : "Colapsar sección de tiras"}
                                    >
                                        <PaletteChevronIcon expanded={!isStripeSectionCollapsed} />
                                    </button>
                                </div>
                            </>
                        ) : (() => {
                            const groupKey = String(group.key);
                            const options = group.options || [];
                            const isCollapsible = options.length > COLLAPSIBLE_SECTION_OPTION_LIMIT;
                            const isCollapsed = !!collapsedSections[groupKey];
                            const hiddenCount = Math.max(0, options.length - SECTION_PREVIEW_OPTION_LIMIT);
                            return (
                                <>
                                    <div className={styles.partGroupHeader}>
                                        <div className={styles.partGroupTitleWrap}>
                                            <h5 className={styles.partGroupTitle}>{group.label}</h5>
                                            {group.activeOptionKey ? (
                                                <span className={styles.partGroupActiveOption}>
                                                    {(group.options || []).find((option) => String(option.key) === String(group.activeOptionKey))?.label || "Activo"}
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>

                                    <div
                                        className={`${styles.partGroupOptions} ${styles.sectionSwipeContainer} ${
                                            isCollapsed ? styles.sectionSwipeCollapsed : styles.sectionSwipeExpanded
                                        }`}
                                    >
                                        {options.map((option, optionIndex) => {
                                            const isActive = option.active
                                                || String(group.activeOptionKey || "") === String(option.key)
                                                || String(activePartKey) === String(option.key);
                                            const canDeselect = option.selected && !option.required && !option.isExclusiveRequiredGroup;
                                            const shouldOpenModal = option.forceModalDebug || option.hasConfigurator;
                                            const selectedMarkColor = FALLBACK_SELECTED_MARK_COLOR;
                                            const selectedColorLabel = option.selectedColor
                                                ? sanitizeColorName(option.selectedColor.name, "Color")
                                                : "\u00A0";
                                            const ringBackground = buildRingBackground(option.ringColors || []);
                                            const statusClass = option.complete
                                                ? styles.partIconOptionComplete
                                                : styles.partIconOptionIncomplete;
                                            const showMissingColorWarning = shouldShowMissingColorWarning(option);

                                            return (
                                                <div
                                                    key={`${group.key}-${option.key}`}
                                                    className={`${styles.partIconOptionCard} ${
                                                        showMissingColorWarning ? styles.partIconOptionCardWarning : ""
                                                    }`}
                                                    data-option-index={optionIndex + 1}
                                                >
                                                    {showMissingColorWarning ? (
                                                        <span
                                                            className={styles.partIconWarningBadge}
                                                            aria-label={`Falta color en ${option.label}`}
                                                            title={`Falta color en ${option.label} — tocá para elegirlo`}
                                                            style={{ cursor: "pointer" }}
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                onSelectActivePart?.(option.key);
                                                                setOpenOptionKey(String(option.key));
                                                            }}
                                                        >
                                                            <ColorWarningIcon />
                                                        </span>
                                                    ) : null}
                                                    <button
                                                        type="button"
                                                        className={`${styles.partIconOption} ${
                                                            option.selected ? styles.partIconOptionSelected : ""
                                                        } ${isActive ? styles.partIconOptionActive : ""} ${statusClass}`}
                                                        aria-pressed={option.selected}
                                                        onClick={() => {
                                                            onSelectActivePart?.(option.key);
                                                            if (!option.selected) {
                                                                // UX 2026-07-05 (dueño): elegir una variante NO interrumpe con el
                                                                // modal de color — se sigue diseñando y el color se define después
                                                                // (el badge "falta color" queda como recordatorio; tocar la pieza
                                                                // ya seleccionada abre la configuración).
                                                                onTogglePart?.(option.key, true);
                                                                return;
                                                            }
                                                            if (shouldOpenModal) {
                                                                setOpenOptionKey(String(option.key));
                                                            }
                                                        }}
                                                        title={option.complete ? option.label : `${option.label}: falta ${option.missing.join(", ")}`}
                                                    >
                                                        <span
                                                            className={`${styles.partIconCircle} ${ringBackground ? styles.partIconCircleWithRing : ""}`}
                                                            style={ringBackground ? { background: ringBackground } : undefined}
                                                        >
                                                            <span
                                                                className={`${styles.partIconInner} ${ringBackground ? styles.partIconInnerWithRing : ""}`}
                                                            >
                                                                {option.iconUrl ? (
                                                                    <img
                                                                        src={option.iconUrl}
                                                                        alt={option.label}
                                                                        className={styles.partIconImage}
                                                                    />
                                                                ) : (
                                                                    <img
                                                                        src={partIconFallbackImg}
                                                                        alt=""
                                                                        className={styles.partIconImage}
                                                                    />
                                                                )}
                                                            </span>
                                                            {option.selected ? (
                                                                <span
                                                                    className={styles.partIconSelectedMark}
                                                                    style={{
                                                                        backgroundColor: selectedMarkColor,
                                                                        color: getSelectedMarkTextColor(selectedMarkColor),
                                                                    }}
                                                                >
                                                                    ✓
                                                                </span>
                                                            ) : null}
                                                        </span>
                                                        <span className={styles.partIconLabel}>{option.label}</span>
                                                        {option.showMaterialFeedback ? (
                                                            <span
                                                                className={`${styles.partIconColorLabel} ${
                                                                    option.selectedColor ? styles.partIconColorLabelActive : ""
                                                                }`}
                                                                title={option.selectedColor?.name || ""}
                                                                aria-hidden={!option.selectedColor}
                                                            >
                                                                {selectedColorLabel}
                                                            </span>
                                                        ) : null}
                                                    </button>
                                                    {canDeselect ? (
                                                        <button
                                                            type="button"
                                                            className={styles.partIconDeleteBtn}
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                if (String(openOptionKey || "") === String(option.key)) {
                                                                    setOpenOptionKey(null);
                                                                }
                                                                onTogglePart?.(option.key, false);
                                                            }}
                                                            aria-label={`Deseleccionar ${option.label}`}
                                                            title={`Deseleccionar ${option.label}`}
                                                        >
                                                            <svg
                                                                viewBox="0 0 24 24"
                                                                className={styles.partIconDeleteSvg}
                                                                aria-hidden="true"
                                                            >
                                                                <path
                                                                    d="M9 3h6l1 2h4v2H4V5h4l1-2Zm1 6h2v8h-2V9Zm4 0h2v8h-2V9ZM7 9h2v8H7V9Zm1 11h8a2 2 0 0 0 2-2V8H6v10a2 2 0 0 0 2 2Z"
                                                                    fill="currentColor"
                                                                />
                                                            </svg>
                                                        </button>
                                                    ) : null}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {isCollapsed && hiddenCount > 0 ? (
                                        <div className={styles.sectionMoreBadge}>+{hiddenCount}</div>
                                    ) : null}
                                    {isCollapsible ? (
                                        <div className={styles.sectionChevronRow}>
                                            <button
                                                type="button"
                                                className={styles.sectionChevronBtn}
                                                onClick={() =>
                                                    setCollapsedSections((prev) => ({
                                                        ...prev,
                                                        [groupKey]: !prev[groupKey],
                                                    }))
                                                }
                                                aria-label={isCollapsed ? "Desplegar sección" : "Colapsar sección"}
                                                title={isCollapsed ? "Desplegar sección" : "Colapsar sección"}
                                            >
                                                <PaletteChevronIcon expanded={!isCollapsed} />
                                            </button>
                                        </div>
                                    ) : null}
                                </>
                            );
                        })()}
                    </section>
                        ) : null}
                    </div>
                );})}
            </div>
            {filteredPartGroups.length === 0 && !stripeWizard?.available ? (
                <div className={styles.emptyState}>
                    No hay partes que coincidan con "{searchTerm}".
                </div>
            ) : null}
            {isStripeTutorialOpen ? (
                <div className={styles.confirmOverlay} onClick={() => setIsStripeTutorialOpen(false)}>
                    <div
                        className={styles.stripeTutorialModalCard}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Tutorial de tiras"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className={styles.stripeTutorialModalHeader}>
                            <h4 className={styles.stripeTutorialModalTitle}>Tutorial de Tiras</h4>
                            <button
                                type="button"
                                className={styles.partOptionModalClose}
                                onClick={() => setIsStripeTutorialOpen(false)}
                                aria-label="Cerrar tutorial de tiras"
                            >
                                ×
                            </button>
                        </div>
                        <StripeTutorialContent />
                    </div>
                </div>
            ) : null}

            <PartColorModal
                option={openOption}
                onClose={() => setOpenOptionKey(null)}
                onSelectActivePart={onSelectActivePart}
                onTogglePart={onTogglePart}
                onSelectMaterial={onSelectMaterial}
                onFetchColors={onFetchColors}
                onTogglePalette={onTogglePalette}
                onSelectColor={handleColorApplied}
                onClearColor={onClearColor}
                isDualPaintEnabled={isDualPaintEnabled}
                onToggleDualPaint={onToggleDualPaint}
            />
        </section>
    );
};

export default PartMaterialView;
